/**
 * The way into a client's work: Clients → the client's module page → the
 * module's hub → its Documents or its Audits.
 *
 * Pure functions: the merged client list, where each step leads, what a role
 * sees on each, and the breadcrumb trails.
 * One path for every role; a role simply has fewer cards.
 */

import {
	MODULE_LABELS,
	MODULE_PARAM,
	clientModuleBase,
	readModule,
	withModule,
	type ModuleCode,
} from './modules';

/** The fields of a client record the flow reads. */
interface ListedClient {
	pkClientId: string;
	name: string;
	files?: number;
	audits?: number;
}

/**
 * Every client of the firm, from the per-module lists (each client is in every
 * module's list). A client appears once, its `files` and `audits` summed
 * across the lists. Lists not loaded yet are skipped. Sorted by name.
 */
export function mergeClientLists<T extends ListedClient>(
	lists: ReadonlyArray<readonly T[] | undefined>,
): T[] {
	const byId = new Map<string, T>();
	for (const list of lists) {
		for (const client of list ?? []) {
			const seen = byId.get(client.pkClientId);
			if (!seen) {
				byId.set(client.pkClientId, { ...client });
				continue;
			}
			byId.set(client.pkClientId, {
				...seen,
				files: (seen.files ?? 0) + (client.files ?? 0),
				audits: (seen.audits ?? 0) + (client.audits ?? 0),
			});
		}
	}
	return [...byId.values()].sort((x, y) => x.name.localeCompare(y.name));
}

/** A client's module page. Carries no module. */
export function modulePagePath(clientId: string): string {
	return `/clients/${clientId}/modules`;
}

/** The route of a client's hub; `:module` is the code in lower case. Every
 *  page of the client in that module lives below it. */
export const HUB_ROUTE = '/clients/:clientId/modules/:module';

/** A client's hub in one module: the Data files and Audits cards. The module
 *  is in the path (`/clients/1/modules/o2c`), not the query. */
export function hubPath(clientId: string, module: ModuleCode): string {
	return clientModuleBase(clientId, module);
}

/** The client's data files in a module. */
export function documentsPath(clientId: string, module: ModuleCode): string {
	return `${clientModuleBase(clientId, module)}/data-files`;
}

/** The client's audits in a module. */
export function auditsPath(clientId: string, module: ModuleCode): string {
	return `${clientModuleBase(clientId, module)}/audits`;
}

/** One audit's workspace, under its client and module. */
export function auditPath(audit: {
	pkAuditId: string;
	fkClientId: string;
	module: ModuleCode;
}): string {
	return `${auditsPath(audit.fkClientId, audit.module)}/${audit.pkAuditId}`;
}

/** What a role may open: the Data files half (intake) and the Audits half. */
export interface FlowAccess {
	documents: boolean;
	audits: boolean;
}

/** A role's access from its permissions. */
export function flowAccess(
	permissions:
		{ accessIntake?: boolean; accessAudits?: boolean } | null | undefined,
): FlowAccess {
	return {
		documents: Boolean(permissions?.accessIntake),
		audits: Boolean(permissions?.accessAudits),
	};
}

/** The hub's cards, in order, for this role: Data files first. */
export function hubCards(
	access: FlowAccess,
	clientId: string,
	module: ModuleCode,
): Array<{ key: 'documents' | 'audits'; label: string; to: string }> {
	return [
		...(access.documents
			? [
					{
						key: 'documents' as const,
						label: 'Data files',
						to: documentsPath(clientId, module),
					},
				]
			: []),
		...(access.audits
			? [
					{
						key: 'audits' as const,
						label: 'Audits',
						to: auditsPath(clientId, module),
					},
				]
			: []),
	];
}

/** A client's counts in one module, from that module's client list (null
 *  while the list is loading). A client not in the list is not set up: 0. */
export function moduleTally(
	list: ReadonlyArray<ListedClient> | undefined,
	clientId: string,
): { documents: number; audits: number } | null {
	if (!list) return null;
	const entry = list.find((row) => row.pkClientId === clientId);
	return { documents: entry?.files ?? 0, audits: entry?.audits ?? 0 };
}

/** "4 documents · 2 audits", only the halves the role may use. */
export function tallyText(
	tally: { documents: number; audits: number },
	access: FlowAccess,
): string {
	const plural = (n: number, word: string) =>
		`${n.toLocaleString('en-IN')} ${word}${n === 1 ? '' : 's'}`;
	return [
		access.documents ? plural(tally.documents, 'data file') : null,
		access.audits ? plural(tally.audits, 'audit') : null,
	]
		.filter(Boolean)
		.join(' · ');
}

/** A breadcrumb, as `PageHeader` takes it. */
export interface FlowCrumb {
	label: string;
	to: string;
}

/**
 * The trail every page under one client in one module starts with:
 * Clients › {client} › {Module}. The client crumb goes to its module page;
 * the module crumb to the hub.
 */
export function clientModuleCrumbs(
	client: { id: string; name?: string | null },
	module: ModuleCode,
): FlowCrumb[] {
	return [
		{ label: 'Clients', to: '/clients' },
		{ label: client.name || 'Client', to: modulePagePath(client.id) },
		{ label: MODULE_LABELS[module], to: hubPath(client.id, module) },
	];
}

/** … › Data files: the trail for the documents side. */
export function documentsCrumbs(
	client: { id: string; name?: string | null },
	module: ModuleCode,
): FlowCrumb[] {
	return [
		...clientModuleCrumbs(client, module),
		{ label: 'Data files', to: documentsPath(client.id, module) },
	];
}

/** … › Audits: the trail for the audits side. */
export function auditsCrumbs(
	client: { id: string; name?: string | null },
	module: ModuleCode,
): FlowCrumb[] {
	return [
		...clientModuleCrumbs(client, module),
		{ label: 'Audits', to: auditsPath(client.id, module) },
	];
}

/**
 * A crumb's href: one that already names a module keeps it (a record's own
 * module is the right one, even before the URL follows it); any other gets
 * the current module, so a way back stays in the module. Old-style intake and
 * audits paths come out under their client and module (`withModule`).
 */
export function crumbHref(to: string, current: ModuleCode): string {
	const queryAt = to.indexOf('?');
	if (queryAt !== -1) {
		const hashAt = to.indexOf('#', queryAt);
		const query = to.slice(queryAt + 1, hashAt === -1 ? undefined : hashAt);
		if (new URLSearchParams(query).has(MODULE_PARAM)) {
			return withModule(to, readModule(query));
		}
	}
	return withModule(to, current);
}
